import { useMemo, useState } from 'react';
import type { DataSourceSwapsDto } from '@/api/types/swapsScanner';

export interface SwapRow {
  instrument: string;
  dataSources: DataSourceSwapsDto[];
  identical?: string;
  longStatus?: string | null;
  shortStatus?: string | null;
}

interface Props {
  rows: SwapRow[];
  showIdentical?: boolean;
  showStatus?: boolean;
  excelFileName: string;
  loading?: boolean;
  emptyMessage?: string;
}

const PAGE_SIZES = [5, 10, 25, 50];
// Swap points display like legacy: trim trailing zeros (3, -19, 2.5), not 3.00.
const numCell = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '' : v.toLocaleString('en-US', { maximumFractionDigits: 2 }));
const statusClass = (s: string | null | undefined) => (s === 'Better' ? 'text-success' : s === 'Worse' ? 'text-danger' : 'text-muted');

// Grouped-header table for Swaps Scanner: a column GROUP per connector/broker (dynamic,
// derived from the rows), each group = Longs / Shorts / Type. Mirrors the legacy
// DataTableColumnHelper.FromDtoWithGroups layout (searching + paging + csv "excel").
export function GroupedSwapsTable({ rows, showIdentical, showStatus, excelFileName, loading, emptyMessage = 'No data.' }: Props) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);

  // Column groups = union of rows' dataSources by key, first-seen order preserved.
  const groups = useMemo(() => {
    const seen = new Map<string, { key: string; title: string }>();
    rows.forEach((r) => (r.dataSources ?? []).forEach((d) => { if (!seen.has(d.key)) seen.set(d.key, { key: d.key, title: d.title }); }));
    return [...seen.values()];
  }, [rows]);

  const filtered = rows;

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const current = Math.min(page, pageCount - 1);
  const pageRows = filtered.slice(current * pageSize, current * pageSize + pageSize);
  const total = filtered.length;
  const start = current * pageSize;

  // Full-numbers pagination window (identical to DataGrid).
  const pageNums = useMemo<(number | 'ellipsis')[]>(() => {
    const last = pageCount - 1;
    const DELTA = 2;
    let lo = current - DELTA;
    let hi = current + DELTA;
    if (lo < 0) { hi += -lo; lo = 0; }
    if (hi > last) { lo -= hi - last; hi = last; }
    lo = Math.max(0, lo);
    hi = Math.min(last, hi);
    const out: (number | 'ellipsis')[] = [];
    if (lo > 0) { out.push(0); if (lo > 1) out.push('ellipsis'); }
    for (let i = lo; i <= hi; i++) out.push(i);
    if (hi < last) { if (hi < last - 1) out.push('ellipsis'); out.push(last); }
    return out;
  }, [current, pageCount]);

  const bySource = (r: SwapRow) => {
    const m = new Map<string, DataSourceSwapsDto>();
    (r.dataSources ?? []).forEach((d) => m.set(d.key, d));
    return m;
  };

  const exportCsv = () => {
    const head = ['Instrument'];
    groups.forEach((g) => head.push(`${g.title} Longs`, `${g.title} Shorts`, `${g.title} Type`));
    if (showIdentical) head.push('Identical');
    if (showStatus) head.push('Long Status', 'Short Status');
    const lines = [head.join(',')];
    filtered.forEach((r) => {
      const m = bySource(r);
      const cells: string[] = [r.instrument];
      groups.forEach((g) => {
        const d = m.get(g.key);
        cells.push(numCell(d?.longs), numCell(d?.shorts), d?.swapMode ?? '');
      });
      if (showIdentical) cells.push(r.identical ?? '');
      if (showStatus) cells.push(r.longStatus ?? '', r.shortStatus ?? '');
      lines.push(cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${excelFileName}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const leadCols = 1; // Instrument
  const tailCols = (showIdentical ? 1 : 0) + (showStatus ? 2 : 0);
  const totalCols = leadCols + groups.length * 3 + tailCols;

  return (
    <div className="card custom-card mb-0">
      <div className="card-body p-0">
        <div className="finkitDatatable">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-2 py-1">
          <div className="d-flex align-items-center gap-2">
            <label className="d-inline-flex align-items-center gap-1 mb-0 small text-nowrap">
              Show
              <select className="form-select form-select-sm d-inline-block w-auto" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}>
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              entries
            </label>
          </div>
          <div className="d-flex align-items-center gap-2 column-toggle-wrapper">
            <button type="button" className="btn btn-sm btn-outline-primary" title="Export to Excel" onClick={exportCsv} disabled={!filtered.length}>
              <i className="fa fa-download" />
            </button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table dataTable table-striped table-hover text-nowrap mb-0 w-100">
            <thead>
              <tr>
                <th rowSpan={2} className="align-middle">Instrument</th>
                {groups.map((g) => <th key={g.key} colSpan={3} className="text-center">{g.title}</th>)}
                {showIdentical && <th rowSpan={2} className="align-middle text-center">Identical</th>}
                {showStatus && <th rowSpan={2} className="align-middle text-center">Long Status</th>}
                {showStatus && <th rowSpan={2} className="align-middle text-center">Short Status</th>}
              </tr>
              <tr>
                {groups.map((g) => [
                  <th key={`${g.key}-l`} className="text-end">Longs</th>,
                  <th key={`${g.key}-s`} className="text-end">Shorts</th>,
                  <th key={`${g.key}-t`} className="text-center">Type</th>,
                ])}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={totalCols} className="text-center py-4"><i className="fa fa-spinner fa-spin me-1" />Loading…</td></tr>
              ) : pageRows.length ? (
                pageRows.map((r, i) => {
                  const m = bySource(r);
                  return (
                    <tr key={`${r.instrument}-${i}`}>
                      <td className="fw-semibold">{r.instrument}</td>
                      {groups.map((g) => {
                        const d = m.get(g.key);
                        return [
                          <td key={`${g.key}-l`} className="text-end">{numCell(d?.longs)}</td>,
                          <td key={`${g.key}-s`} className="text-end">{numCell(d?.shorts)}</td>,
                          <td key={`${g.key}-t`} className="text-center">{d?.swapMode ?? ''}</td>,
                        ];
                      })}
                      {showIdentical && <td className="text-center">{r.identical ?? ''}</td>}
                      {showStatus && <td className={`text-center ${statusClass(r.longStatus)}`}>{r.longStatus ?? ''}</td>}
                      {showStatus && <td className={`text-center ${statusClass(r.shortStatus)}`}>{r.shortStatus ?? ''}</td>}
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan={totalCols} className="text-center text-muted py-4">{emptyMessage}</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-2 py-1">
          <div className="dt-info small text-muted">
            {total === 0 ? 'Showing 0 to 0 of 0 entries' : `Showing ${start + 1} to ${Math.min(start + pageSize, total)} of ${total} entries`}
          </div>
          <nav className="dt-paging">
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item${current === 0 ? ' disabled' : ''}`}>
                <button type="button" className="page-link" title="First" onClick={() => setPage(0)}>«</button>
              </li>
              <li className={`page-item${current === 0 ? ' disabled' : ''}`}>
                <button type="button" className="page-link" title="Previous" onClick={() => setPage(current - 1)}>‹</button>
              </li>
              {pageNums.map((p, i) =>
                p === 'ellipsis' ? (
                  <li key={`e${i}`} className="page-item disabled"><span className="page-link">…</span></li>
                ) : (
                  <li key={p} className={`page-item${p === current ? ' active' : ''}`}>
                    <button type="button" className="page-link" onClick={() => setPage(p)}>{p + 1}</button>
                  </li>
                ),
              )}
              <li className={`page-item${current >= pageCount - 1 ? ' disabled' : ''}`}>
                <button type="button" className="page-link" title="Next" onClick={() => setPage(current + 1)}>›</button>
              </li>
              <li className={`page-item${current >= pageCount - 1 ? ' disabled' : ''}`}>
                <button type="button" className="page-link" title="Last" onClick={() => setPage(pageCount - 1)}>»</button>
              </li>
            </ul>
          </nav>
        </div>
        </div>
      </div>
    </div>
  );
}
